import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Link,
  Text,
  useToast,
  Divider,
  Checkbox,
  SimpleGrid,
  Spinner,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { Form } from "@unform/web";
import { FormHandles } from "@unform/core";
import { useEffect, useMemo, useRef, useState } from "react";
import InputField from "../../../components/InputField";
import SelectField from "../../../components/SelectField";
import * as Yup from "yup";
import { warnValidation } from "../../../services/utils";
import TextareaField from "../../../components/TextareaField";
import {
  getPurchaseOrder,
  savePurchaseOrder,
  updatePurchaseOrder,
} from "../../../services/purchaseOrderService";
import SearchableSelect from "../../../components/SearchableSelect";
import { getCompanies } from "../../../services/companyService";
import { getSuppliers } from "../../../services/supplierService";
import { FaAngleRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../../components/Layout";
import { crm } from "../../../theme";
import { useReactToPrint } from "react-to-print";
import PurchaseOrderPrint from "./components/PurchaseOrderPrint";
import ApproveModal from "../../processes/components/approveModal";
import { canManagePurchaseOrders } from "../permissions";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { FaHistory, FaPlus } from "react-icons/fa";
import { DateTime } from "luxon";
import { incotermOptions } from "../../../utils/incoterms";
import {
  clientExchangeTermOptions,
  clientNationalizationTermOptions,
  supplierExchangeTermOptions,
  supplierNationalizationTermOptions,
  withLegacyTerm,
} from "../../../utils/paymentTerms";

const paymentTermDaysFields = [
  "firstExchangeSupplierTermDays",
  "secondExchangeSupplierTermDays",
  "nationalizationSupplierTermDays",
  "firstExchangeClientTermDays",
  "secondExchangeClientTermDays",
  "nationalizationClientTermDays",
];

const currencyFields = [
  "financialCost",
  "finalInvoiceValue",
  "totalFobValue",
  "firstExchangeDisbursement",
  "secondExchangeDisbursement",
  "clearanceDisbursement",
  "invoiceValueDollar",
  "invoiceValueReal",
  "freightAllIn",
];

const PaymentTermField = ({
  label,
  termName,
  valueName,
  options,
}: {
  label: string;
  termName: string;
  valueName: string;
  options: { label: string; value: string }[];
}) => (
  <FormControl>
    <Box>
      <FormLabel>{label}</FormLabel>
      <SelectField name={termName} placeholder="Condição" options={options} />
      <Flex gap="8px" mt="8px">
        <InputField
          type="number"
          min={0}
          name={`${termName}Days`}
          placeholder="Prazo (dias)"
        />
        <InputField
          type="number"
          step="0.01"
          name={valueName}
          placeholder={label}
        />
      </Flex>
    </Box>
  </FormControl>
);

const SavePurchaseOrder = () => {
  const formRef = useRef<FormHandles>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultPurchaseOrder, setDefaultPurchaseOrder] = useState<any>();
  const [isPrinting, setIsPrinting] = useState(false);
  const [client, setClient] = useState<{
    id: string;
    name: string;
    cnpj: string;
  }>();
  const navigate = useNavigate();
  const auth = useAuthUser<any>();
  const history = useDisclosure();
  const discardDialog = useDisclosure();
  const cancelDiscardRef = useRef<HTMLButtonElement>(null);
  // Rejeitar uma alteração recarrega o form (o valor desfeito precisa aparecer); o que foi digitado e
  // não salvo se perderia sem aviso. Snapshot tirado quando o form termina de carregar.
  const savedSnapshot = useRef<string>();
  const snapshotFor = useRef<any>();

  // Resource Origin checkboxes state
  const [resourceOrigin, setResourceOrigin] = useState({
    ownCash: false,
    reserveCash: false,
    finimp: false,
    letterOfCredit: false,
  });

  useEffect(() => {
    const getClients = async () => {
      const response = await getCompanies(false);
      if (response) setClients(response);
    };
    const getSavedSuppliers = async () => {
      const response = await getSuppliers();
      if (response) {
        setSuppliers(
          [...response].sort((a, b) =>
            a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
          )
        );
      }
    };
    getClients();
    getSavedSuppliers();
  }, []);

  const schema = Yup.object().shape({
    clientId: Yup.string().required("Este campo é obrigatório."),
    invoiceRef: Yup.string().required("Este campo é obrigatório."),
    supplier: Yup.string().required("Este campo é obrigatório."),
    supplierInvoiceDate: Yup.date().required("Este campo é obrigatório."),
    originPort: Yup.string().required("Este campo é obrigatório."),
    incoterm: Yup.string().required("Este campo é obrigatório."),
    destinationPort: Yup.string().required("Este campo é obrigatório."),
    productNcm: Yup.string().required("Este campo é obrigatório."),
    containerQty: Yup.string().required("Este campo é obrigatório."),
    margin: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .typeError("Informe um número válido.")
      .required("Este campo é obrigatório."),
  });

  const location = useLocation();
  const id = new URLSearchParams(location.search).get("id");
  const toast = useToast();
  const [purchaseOrder, setPurchaseOrder] = useState<any>();
  const [loadingSave, setLoadingSave] = useState(false);
  const [clientError, setClientError] = useState("");
  const [supplierError, setSupplierError] = useState("");
  const [suppliers, setSuppliers] = useState<{ name: string }[]>([]);
  const [supplier, setSupplier] = useState("");
  const defaultSupplierOption = useMemo(
    () =>
      defaultPurchaseOrder?.supplier
        ? {
            value: defaultPurchaseOrder.supplier,
            label: defaultPurchaseOrder.supplier,
          }
        : undefined,
    [defaultPurchaseOrder?.supplier]
  );
  const [clients, setClients] = useState<
    {
      id: string;
      name: string;
      cnpj: string;
    }[]
  >([]);
  const defaultClientOption = useMemo(
    () => client ? { value: client.id, label: client.name } : undefined,
    [client]
  );
  useEffect(() => {
    if (!defaultPurchaseOrder || !clients.length) return;
    const savedClient =
      clients.find((c) => c.name === defaultPurchaseOrder.client) ??
      (defaultPurchaseOrder.cnpj
        ? clients.find((c) => c.cnpj === defaultPurchaseOrder.cnpj)
        : undefined);
    setClient(savedClient);
  }, [clients, defaultPurchaseOrder]);
  const get = async () => {
    setIsLoading(true);
    const response = await getPurchaseOrder(id ?? "");
    if (response) {
      setSupplier(response.supplier ?? "");
      setResourceOrigin({
        ownCash: response.ownCash ?? false,
        reserveCash: response.reserveCash ?? false,
        finimp: response.finimp ?? false,
        letterOfCredit: response.letterOfCredit ?? false,
      });
      setDefaultPurchaseOrder({
        ...response,
        ...Object.fromEntries(
          currencyFields.map((field) => [
            field,
            response[field] == null
              ? ""
              : Number(response[field]).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
          ])
        ),
        supplierInvoiceDate: DateTime.fromISO(
          response.supplierInvoiceDate
        ).toFormat("yyyy-LL-dd"),
        firstExchangeExpectedDate: DateTime.fromISO(
          response.firstExchangeExpectedDate
        ).toFormat("yyyy-LL-dd"),
        secondExchangeExpectedDate: DateTime.fromISO(
          response.secondExchangeExpectedDate
        ).toFormat("yyyy-LL-dd"),
        clearanceExpectedDate: DateTime.fromISO(
          response.clearanceExpectedDate
        ).toFormat("yyyy-LL-dd"),
        invoiceExpectedDate: DateTime.fromISO(
          response.invoiceExpectedDate
        ).toFormat("yyyy-LL-dd"),
        shipmentDate: DateTime.fromISO(response.shipmentDate).toFormat(
          "yyyy-LL-dd"
        ),
        arrivalDate: DateTime.fromISO(response.arrivalDate).toFormat(
          "yyyy-LL-dd"
        ),
      });
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (id) {
      get();
    }
  }, [id]);

  const formState = () =>
    JSON.stringify({
      form: formRef.current?.getData(),
      client: client?.id,
      supplier,
      resourceOrigin,
    });

  // Uma vez por PO carregada, depois que o cliente salvo foi resolvido (lista de clientes é assíncrona).
  // Não refaz quando o usuário troca o cliente — isso é edição, não carga.
  useEffect(() => {
    if (!id || isLoading || !defaultPurchaseOrder || !client) return;
    if (snapshotFor.current === defaultPurchaseOrder) return;
    // Espera o unform registrar os campos do form recém-montado. Marca a PO só quando o snapshot
    // é tirado: se o timer for cancelado por um re-render, a próxima execução tenta de novo.
    const timer = setTimeout(() => {
      snapshotFor.current = defaultPurchaseOrder;
      savedSnapshot.current = formState();
    }, 0);
    return () => clearTimeout(timer);
  }, [id, isLoading, defaultPurchaseOrder, client]);

  const openHistory = () => {
    if (savedSnapshot.current && formState() !== savedSnapshot.current) {
      discardDialog.onOpen();
      return;
    }
    history.onOpen();
  };

  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Ordem de Compra - ${client?.name || ""}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 5mm; 
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
          print-color-adjust: exact;
        }
        .no-print {
          display: none !important;
        }
        .page-break {
          page-break-before: always;
        }
        .avoid-break {
          page-break-inside: avoid;
        }
      }
    `,
    onBeforePrint: async () => {
      setIsPrinting(true);
    },
    onAfterPrint: () => {
      setIsPrinting(false);
      navigate("/purchaseOrders");
    },
  });

  const handleSubmit = async (data: any) => {
    try {
      formRef.current?.setErrors({});
      setClientError("");
      setSupplierError("");
      await schema.validate(
        {
          ...data,
          clientId: client?.id,
          supplier: supplier || data.supplier,
        },
        { abortEarly: false }
      );
      const body: any = {
        ...data,
        documentDate: data.documentDate ?? defaultPurchaseOrder?.documentDate,
        clientId: client?.id,
        client: client?.name,
        cnpj: client?.cnpj,
        supplier: supplier || data.supplier,
        ...resourceOrigin,
        taxRegime: data.taxRegime || null,
        stOrDifalDestination: data.stOrDifalDestination || null,
        financialCost:
          data.financialCost?.replaceAll(".", "").replace(",", ".") || null,
        nationalizationFactor: data.nationalizationFactor || null,
        ...Object.fromEntries(
          paymentTermDaysFields.map((field) => [
            field,
            data[field] ? Number(data[field]) : null,
          ])
        ),
        finalInvoiceValue: data.finalInvoiceValue
          ?.replaceAll(".", "")
          .replace(",", "."),
        totalFobValue: data.totalFobValue
          ?.replaceAll(".", "")
          .replace(",", "."),
        firstExchangeDisbursement: data.firstExchangeDisbursement
          ?.replaceAll(".", "")
          .replace(",", "."),
        secondExchangeDisbursement: data.secondExchangeDisbursement
          ?.replaceAll(".", "")
          .replace(",", "."),
        clearanceDisbursement: data.clearanceDisbursement
          ?.replaceAll(".", "")
          .replace(",", "."),
        invoiceValueDollar: data.invoiceValueDollar
          ?.replaceAll(".", "")
          .replace(",", "."),
        invoiceValueReal: data.invoiceValueReal
          ?.replaceAll(".", "")
          .replace(",", "."),
        freightAllIn: data.freightAllIn?.replaceAll(".", "").replace(",", "."),
      };
      // Unform includes empty inputs as ""; the API expects numeric and date
      // fields to be omitted when they have no value.
      Object.keys(body).forEach((field) => {
        if (body[field] === "" || body[field] === undefined) delete body[field];
      });
      setLoadingSave(true);
      const response: any = id
        ? await updatePurchaseOrder({ ...body, id } as any)
        : await savePurchaseOrder(body);

      if (response) {
        toast({
          title: "Ordem de compra salva.",
          description:
            "Ordem de compra salva e enviada para os respectivos e-mails para assinatura.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Sucesso com notificações = avisos do backend (ex.: CNPJ sem empresa cadastrada).
        (response.data?.notifications ?? []).forEach((warning: string) =>
          toast({
            title: "Atenção",
            description: warning,
            status: "warning",
            duration: 9000,
            isClosable: true,
          })
        );

        setPurchaseOrder(response.data.purchaseOrder);
      }
    } catch (err: any) {
      warnValidation(err, formRef.current);
      if (err instanceof Yup.ValidationError) {
        setClientError(
          err.inner.find((error) => error.path === "clientId")?.message ?? ""
        );
        setSupplierError(
          err.inner.find((error) => error.path === "supplier")?.message ?? ""
        );
      } else {
        const responseData = err?.response?.data;
        const apiFieldError = Object.values(responseData?.errors ?? {})
          .flat()
          .find((message) => typeof message === "string");

        toast({
          title: "Não foi possível salvar a ordem de compra.",
          description:
            responseData?.notifications?.[0] ??
            apiFieldError ??
            responseData?.message ??
            err?.message ??
            "Tente novamente.",
          status: "error",
          duration: 7000,
          isClosable: true,
        });
      }
    } finally {
      setLoadingSave(false);
    }
  };
  useEffect(() => {
    if (purchaseOrder) {
      handlePrint();
    }
  }, [purchaseOrder]);
  const SectionDivider = ({ title }: { title: string }) => (
    <Box my="24px">
      <Divider />
      <Heading size="md" my="16px" fontWeight="semibold" color={crm.text}>
        {title}
      </Heading>
    </Box>
  );

  return (
    <Box height="100%" overflowX="hidden">
      <Layout>
        <Flex
          pb="64px"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          w="100%"
          backgroundColor="var(--main-bg-color)"
        >
          <Flex
            alignItems="center"
            mb="24px"
            pt="0"
            gap="8px"
            mt={{ base: "16px", md: "27px" }}
            width="90%"
          >
            <Link
              color={crm.brand}
              onClick={() => navigate("/purchaseOrders")}
              size="md"
            >
              Ordens de compra
            </Link>
            <FaAngleRight />
            <Text size="md">{id ? "Editar" : "Criar"} ordem de compra</Text>
          </Flex>
          <Flex width={{ base: "95%", md: "65%" }} mb="24px" align="center" gap="12px" flexWrap="wrap">
            <Box w="4px" h="34px" bg={crm.brand} borderRadius="full" />
            <Box flex="1">
              <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                {id ? `Editar ordem de compra${defaultPurchaseOrder?.poNumber ? ` PO ${defaultPurchaseOrder.poNumber}` : ""}` : "Criar ordem de compra"}
              </Text>
              <Text color={crm.textMuted} fontSize="sm" mt="2px">
                {id
                  ? defaultPurchaseOrder?.process?.cvfRef
                    ? "Alterações nos dados de embarque, cliente e valores também atualizam o processo vinculado."
                    : "Esta PO ainda não tem processo vinculado."
                  : "Preencha os dados para criar uma nova ordem de compra."}
              </Text>
            </Box>
            {id && (
              <Flex gap="8px" align="center">
                {defaultPurchaseOrder?.process?.cvfRef ? (
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor={crm.brand}
                    color={crm.brand}
                    onClick={() => navigate(`/process?id=${defaultPurchaseOrder.process.id}`)}
                  >
                    Processo {defaultPurchaseOrder.process.cvfRef}
                  </Button>
                ) : (
                  canManagePurchaseOrders(auth) && (
                    <Button
                      size="sm"
                      leftIcon={<FaPlus />}
                      color="white"
                      backgroundColor={crm.brand}
                      _hover={{ backgroundColor: crm.brandDark }}
                      onClick={() => navigate(`/saveProcess?fromPo=${id}`)}
                    >
                      Criar processo
                    </Button>
                  )
                )}
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FaHistory />}
                  onClick={openHistory}
                >
                  Histórico
                </Button>
              </Flex>
            )}
          </Flex>
          {isLoading ? (
            <Flex h="100vh" w="100vw">
              <Spinner />
            </Flex>
          ) : (
            <Form
              initialData={defaultPurchaseOrder}
              ref={formRef}
              onSubmit={handleSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                alignItems: "center",
              }}
            >
              <Box width={{ base: "95%", md: "65%" }}>
                <Flex
                  backgroundColor={crm.surface}
                  border={`1px solid ${crm.border}`}
                  boxShadow={crm.shadowSm}
                  borderRadius="12px"
                  flexDirection="column"
                  padding={{ base: 0, md: "32px" }}
                >
                  {/* Client Information */}
                  <FormControl isRequired>
                    <Box>
                      <FormLabel>Cliente: </FormLabel>
                      <SearchableSelect
                        placeholder="Selecione o cliente"
                        options={clients.map((c) => ({
                          value: c.id,
                          label: c.name,
                          subLabel: c.cnpj,
                        }))}
                        defaultSelectValue={defaultClientOption}
                        name="clientId"
                        value={client?.id}
                        isInvalid={!!clientError}
                        setValue={(value) => {
                          const selClient = clients.find(
                            (c) => c.id.toLowerCase() === value?.toLowerCase()
                          );
                          setClient(selClient);
                          setClientError("");
                        }}
                      />
                      {clientError && <Text color="red.300">{clientError}</Text>}
                      {client && <Text mt="12px">CNPJ: {client.cnpj}</Text>}
                    </Box>
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <Box mt={10}>
                        <FormLabel>Regime Tributário: </FormLabel>
                        <SelectField
                          name="taxRegime"
                          placeholder="Regime Tributário"
                          options={[
                            { value: "Lucro Real", label: "Lucro Real" },
                            {
                              value: "Lucro Presumido",
                              label: "Lucro Presumido",
                            },
                            {
                              value: "Simples Nacional",
                              label: "Simples Nacional",
                            },
                          ]}
                        />
                      </Box>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <Box mt={10}>
                      <FormLabel>ST ou DIFAL Destino: </FormLabel>
                      <InputField
                        name="stOrDifalDestination"
                        placeholder="ST ou DIFAL Destino"
                      />
                    </Box>
                  </FormControl>

                  {/* <ProcessInput {...{ formRef, processId, setProcessId }} /> */}

                  <SectionDivider title="Informações da Invoice" />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <Box>
                        <FormLabel>Ref. Invoice fornecedor: </FormLabel>
                        <InputField
                          name="invoiceRef"
                          placeholder="Invoice Ref"
                        />
                      </Box>
                    </FormControl>

                    <FormControl isRequired>
                      <Box>
                        <FormLabel>Fornecedor: </FormLabel>
                        <SearchableSelect
                          placeholder="Fornecedor"
                          options={suppliers.map((s) => ({
                            value: s.name,
                            label: s.name,
                          }))}
                          defaultSelectValue={defaultSupplierOption}
                          isInvalid={!!supplierError}
                          setValue={(value) => {
                            setSupplier(value ?? "");
                            setSupplierError("");
                          }}
                        />
                        {supplierError && <Text color="red.300">{supplierError}</Text>}
                        <InputField type="hidden" name="supplier" />
                      </Box>
                    </FormControl>

                    <FormControl isRequired>
                      <Box>
                        <FormLabel>Data Invoice: </FormLabel>
                        <InputField
                          type="date"
                          name="supplierInvoiceDate"
                          placeholder="Data Invoice"
                        />
                      </Box>
                    </FormControl>

                    <FormControl isRequired>
                      <Box>
                        <FormLabel>Porto Origem: </FormLabel>
                        <InputField
                          name="originPort"
                          placeholder="Porto Origem"
                        />
                      </Box>
                    </FormControl>

                    <FormControl isRequired>
                      <Box>
                        <FormLabel>Incoterm: </FormLabel>
                        <SelectField
                          name="incoterm"
                          placeholder="Incoterm"
                          options={
                            defaultPurchaseOrder?.incoterm === "DDU"
                              ? [...incotermOptions, { label: "DDU", value: "DDU" }]
                              : incotermOptions
                          }
                        />
                      </Box>
                    </FormControl>

                    <FormControl isRequired>
                      <Box>
                        <FormLabel>Porto Destino: </FormLabel>
                        <InputField
                          name="destinationPort"
                          placeholder="Porto Destino"
                        />
                      </Box>
                    </FormControl>
                  </SimpleGrid>

                  <SectionDivider title="Informações do Produto" />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <Box>
                        <FormLabel>NCM do Produto: </FormLabel>
                        <InputField
                          name="productNcm"
                          placeholder="NCM do Produto"
                        />
                      </Box>
                    </FormControl>

                    <FormControl isRequired>
                      <Box>
                        <FormLabel>Quantidade de containers: </FormLabel>
                        <InputField
                          name="containerQty"
                          placeholder="Container"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Custo Financeiro: </FormLabel>
                        <InputField
                          mask="currency"
                          name="financialCost"
                          placeholder="Custo Financeiro"
                        />
                      </Box>
                    </FormControl>

                    <FormControl isRequired>
                      <Box>
                        <FormLabel>Margem (%): </FormLabel>
                        <InputField
                          type="number"
                          step="0.01"
                          name="margin"
                          placeholder="Margem"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Fator de Nacionalização: </FormLabel>
                        <InputField
                          type="number"
                          step="0.01"
                          name="nationalizationFactor"
                          placeholder="Fator de Nacionalização"
                        />
                      </Box>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                    <FormControl>
                      <Box>
                        <FormLabel>Valor Final da Invoice: </FormLabel>
                        <InputField
                          mask="currency"
                          name="finalInvoiceValue"
                          placeholder="Valor Final da Invoice"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Valor Total FOB: </FormLabel>
                        <InputField
                          mask="currency"
                          name="totalFobValue"
                          placeholder="Valor Total FOB"
                        />
                      </Box>
                    </FormControl>
                  </SimpleGrid>

                  <SectionDivider title="Origem do Recurso" />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Checkbox
                      isChecked={resourceOrigin.ownCash}
                      onChange={(e) =>
                        setResourceOrigin((prev) => ({
                          ...prev,
                          ownCash: e.target.checked,
                        }))
                      }
                    >
                      Caixa Próprio
                    </Checkbox>
                    <Checkbox
                      isChecked={resourceOrigin.reserveCash}
                      onChange={(e) =>
                        setResourceOrigin((prev) => ({
                          ...prev,
                          reserveCash: e.target.checked,
                        }))
                      }
                    >
                      Caixa Reserva
                    </Checkbox>
                    <Checkbox
                      isChecked={resourceOrigin.finimp}
                      onChange={(e) =>
                        setResourceOrigin((prev) => ({
                          ...prev,
                          finimp: e.target.checked,
                        }))
                      }
                    >
                      FINIMP
                    </Checkbox>
                    <Checkbox
                      isChecked={resourceOrigin.letterOfCredit}
                      onChange={(e) =>
                        setResourceOrigin((prev) => ({
                          ...prev,
                          letterOfCredit: e.target.checked,
                        }))
                      }
                    >
                      Carta de Crédito
                    </Checkbox>
                  </SimpleGrid>

                  <SectionDivider title="Condições de Pagamento - Fornecedor" />

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <PaymentTermField
                      label="1º Câmbio Fornecedor"
                      termName="firstExchangeSupplierTerm"
                      valueName="firstExchangeSupplier"
                      options={withLegacyTerm(
                        supplierExchangeTermOptions,
                        defaultPurchaseOrder?.firstExchangeSupplierTerm
                      )}
                    />
                    <PaymentTermField
                      label="2º Câmbio Fornecedor"
                      termName="secondExchangeSupplierTerm"
                      valueName="secondExchangeSupplier"
                      options={withLegacyTerm(
                        supplierExchangeTermOptions,
                        defaultPurchaseOrder?.secondExchangeSupplierTerm
                      )}
                    />
                    <PaymentTermField
                      label="Nacionalização Fornecedor"
                      termName="nationalizationSupplierTerm"
                      valueName="nationalizationSupplier"
                      options={withLegacyTerm(
                        supplierNationalizationTermOptions,
                        defaultPurchaseOrder?.nationalizationSupplierTerm
                      )}
                    />
                  </SimpleGrid>

                  <SectionDivider title="Condições de Pagamento - Cliente" />

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <PaymentTermField
                      label="1º Câmbio Cliente"
                      termName="firstExchangeClientTerm"
                      valueName="firstExchangeClient"
                      options={withLegacyTerm(
                        clientExchangeTermOptions,
                        defaultPurchaseOrder?.firstExchangeClientTerm
                      )}
                    />
                    <PaymentTermField
                      label="2º Câmbio Cliente"
                      termName="secondExchangeClientTerm"
                      valueName="secondExchangeClient"
                      options={withLegacyTerm(
                        clientExchangeTermOptions,
                        defaultPurchaseOrder?.secondExchangeClientTerm
                      )}
                    />
                    <PaymentTermField
                      label="Nacionalização Cliente"
                      termName="nationalizationClientTerm"
                      valueName="nationalizationClient"
                      options={withLegacyTerm(
                        clientNationalizationTermOptions,
                        defaultPurchaseOrder?.nationalizationClientTerm
                      )}
                    />
                  </SimpleGrid>

                  <SectionDivider title="Operação de Fluxo de Caixa" />

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl>
                      <Box>
                        <FormLabel>1º Câmbio Desembolso: </FormLabel>
                        <InputField
                          mask="currency"
                          name="firstExchangeDisbursement"
                          placeholder="1º Câmbio Desembolso"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>1º Câmbio Prazo Aberto (dias): </FormLabel>
                        <InputField
                          type="number"
                          name="firstExchangeOpenTerm"
                          placeholder="1º Câmbio Prazo Aberto"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>1º Câmbio Data Prevista: </FormLabel>
                        <InputField
                          type="date"
                          name="firstExchangeExpectedDate"
                          placeholder="1º Câmbio Data Prevista"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>2º Câmbio Desembolso: </FormLabel>
                        <InputField
                          mask="currency"
                          name="secondExchangeDisbursement"
                          placeholder="2º Câmbio Desembolso"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>2º Câmbio Prazo Aberto (dias): </FormLabel>
                        <InputField
                          type="number"
                          name="secondExchangeOpenTerm"
                          placeholder="2º Câmbio Prazo Aberto"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>2º Câmbio Data Prevista: </FormLabel>
                        <InputField
                          type="date"
                          name="secondExchangeExpectedDate"
                          placeholder="2º Câmbio Data Prevista"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Desembaraço Desembolso: </FormLabel>
                        <InputField
                          mask="currency"
                          name="clearanceDisbursement"
                          placeholder="Desembaraço Desembolso"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Desembaraço Prazo Aberto (dias): </FormLabel>
                        <InputField
                          type="number"
                          name="clearanceOpenTerm"
                          placeholder="Desembaraço Prazo Aberto"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Desembaraço Data Prevista: </FormLabel>
                        <InputField
                          type="date"
                          name="clearanceExpectedDate"
                          placeholder="Desembaraço Data Prevista"
                        />
                      </Box>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid
                    columns={{ base: 1, md: 3 }}
                    mt="24px"
                    spacing={4}
                  >
                    <FormControl>
                      <Box>
                        <FormLabel>Valor NF Dólar: </FormLabel>
                        <InputField
                          mask="currency"
                          name="invoiceValueDollar"
                          placeholder="Valor NF Dólar"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Valor NF Real: </FormLabel>
                        <InputField
                          mask="currency"
                          name="invoiceValueReal"
                          placeholder="Valor NF Real"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Data Prevista NF: </FormLabel>
                        <InputField
                          type="date"
                          name="invoiceExpectedDate"
                          placeholder="Data Prevista NF"
                        />
                      </Box>
                    </FormControl>
                  </SimpleGrid>

                  <SectionDivider title="Informações de Frete" />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <Box>
                        <FormLabel>Frete All In: </FormLabel>
                        <InputField
                          mask="currency"
                          name="freightAllIn"
                          placeholder="Frete All In"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Dias Free Time: </FormLabel>
                        <InputField
                          type="number"
                          name="freeTimeDays"
                          placeholder="Dias Free Time"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Data Embarque: </FormLabel>
                        <InputField
                          type="date"
                          name="shipmentDate"
                          placeholder="Data Embarque"
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box>
                        <FormLabel>Data Chegada: </FormLabel>
                        <InputField
                          type="date"
                          name="arrivalDate"
                          placeholder="Data Chegada"
                        />
                      </Box>
                    </FormControl>
                  </SimpleGrid>

                  <SectionDivider title="Observações" />

                  <FormControl>
                    <Box>
                      <FormLabel>Observações: </FormLabel>
                      <TextareaField
                        name="observations"
                        placeholder="Observações"
                        rows={4}
                      />
                    </Box>
                  </FormControl>
                </Flex>
              </Box>

              <Flex w="65%" justifyContent="flex-end" marginY="32px" gap="16px">
                <Button
                  width="120px"
                  backgroundColor="transparent"
                  color={crm.brand}
                  borderColor={crm.brand}
                  borderWidth="2px"
                  borderRadius="10px"
                  isLoading={loadingSave}
                  _hover={{ backgroundColor: crm.brandSoft }}
                  onClick={() => navigate(-1)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  isLoading={loadingSave}
                  color="white"
                  borderRadius="10px"
                  width="120px"
                  backgroundColor={crm.brand}
                  _hover={{ backgroundColor: crm.brandDark }}
                >
                  Salvar
                </Button>
              </Flex>
            </Form>
          )}
        </Flex>
      </Layout>
      {purchaseOrder && (
        <Box
          style={{
            position: "fixed",
            top: "-200vh",
            left: "0",
            width: "100%",
            backgroundColor: "#ffffff",
            color: "#000000",
            visibility: isPrinting ? "visible" : "hidden",
            opacity: isPrinting ? 1 : 0,
            zIndex: -1000,
            transformOrigin: "top left",
            overflow: "hidden",
          }}
        >
          <PurchaseOrderPrint {...{ purchaseOrder, printRef }} />
        </Box>
      )}
      <AlertDialog
        isOpen={discardDialog.isOpen}
        leastDestructiveRef={cancelDiscardRef}
        onClose={discardDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Alterações não salvas</AlertDialogHeader>
            <AlertDialogBody>
              Se você reprovar uma alteração no histórico, o formulário é recarregado e o que foi editado
              aqui sem salvar será perdido.
            </AlertDialogBody>
            <AlertDialogFooter gap="8px">
              <Button ref={cancelDiscardRef} onClick={discardDialog.onClose}>
                Voltar
              </Button>
              <Button
                colorScheme="orange"
                onClick={() => {
                  discardDialog.onClose();
                  history.onOpen();
                }}
              >
                Abrir histórico mesmo assim
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {id && (
        <ApproveModal
          isOpen={history.isOpen}
          onClose={history.onClose}
          entityId={id}
          entity="PurchaseOrder"
          title={`Alterações da PO ${defaultPurchaseOrder?.poNumber ?? ""}`}
          reviewAdminChanges
          // Recarrega: o valor desfeito precisa aparecer no formulário (o spinner remonta o form).
          onChanged={get}
        />
      )}
    </Box>
  );
};

export default SavePurchaseOrder;
